import { Button, cn, EmptyState, IconButton, ListSkeleton, ResponsivePopover } from '@nexus/ui';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client';
import type { NotificationItem, NotificationList } from '../api/types';
import { formatDateTime, formatRelative } from '../i18n/format';
import { safePath } from '../lib/paths';
import { setAppBadge } from '../settings/push';

export const notificationsQuery = queryOptions({
  queryKey: ['notifications'],
  queryFn: ({ signal }) => api.get<NotificationList>('/notifications?limit=30', { signal }),
  refetchInterval: 60_000,
});

export function NotificationEntry({
  item,
  onOpen,
}: {
  item: NotificationItem;
  onOpen: (item: NotificationItem) => void;
}) {
  const { t, i18n } = useTranslation();
  const created = new Date(item.createdAt);
  return (
    <li>
      <button
        type="button"
        onClick={() => onOpen(item)}
        className="flex w-full cursor-pointer items-start gap-3 px-4 py-3 text-left transition-colors transition-fast hover:bg-hover"
      >
        <span
          className={cn(
            'mt-1.5 size-2 shrink-0 rounded-full',
            item.readAt ? 'bg-transparent' : 'bg-brand',
          )}
        />
        <span className="min-w-0 flex-1">
          <span className={cn('block text-fg', !item.readAt && 'font-medium')}>
            {t(item.titleKey, item.params)}
          </span>
          <time
            dateTime={item.createdAt}
            title={formatDateTime(created, i18n.language)}
            className="mt-0.5 block text-xs text-fg-subtle"
          >
            {formatRelative(created, i18n.language, t('common.relative.justNow'))}
          </time>
        </span>
        {!item.readAt && <span className="sr-only">{t('shell.notifications.unread')}</span>}
      </button>
    </li>
  );
}

export function useOpenNotification() {
  const client = useQueryClient();
  const navigate = useNavigate();
  return async (item: NotificationItem) => {
    if (!item.readAt) {
      await api.post(`/notifications/${item.id}/read`).catch(() => undefined);
      void client.invalidateQueries(notificationsQuery);
    }
    const target = safePath(item.link);
    if (target) await navigate({ to: target });
  };
}

/** Bell with unread counter and the latest notifications. */
export function NotificationsPanel() {
  const { t } = useTranslation();
  const client = useQueryClient();
  const [open, setOpen] = useState(false);
  const { data, isPending } = useQuery(notificationsQuery);
  const openNotification = useOpenNotification();
  const markAll = useMutation({
    mutationFn: () => api.post('/notifications/read-all'),
    onSettled: () => client.invalidateQueries(notificationsQuery),
  });
  const unread = data?.unread ?? 0;
  // The number on the app icon follows the bell; a push message sets it while the app is closed.
  useEffect(() => {
    if (data) setAppBadge(data.unread);
  }, [data]);

  return (
    <ResponsivePopover
      open={open}
      onOpenChange={setOpen}
      title={t('shell.notifications.title')}
      headerActions={
        unread > 0 ? (
          <Button
            size="sm"
            variant="ghost"
            loading={markAll.isPending}
            onClick={() => markAll.mutate()}
          >
            {t('shell.notifications.markAllRead')}
          </Button>
        ) : null
      }
      trigger={
        <IconButton
          icon="bell"
          count={unread}
          label={
            unread > 0
              ? t('shell.notifications.openUnread', { count: unread })
              : t('shell.notifications.open')
          }
        />
      }
    >
      {isPending ? (
        <ListSkeleton card={false} rows={3} className="p-4" />
      ) : data && data.items.length > 0 ? (
        <ul className="divide-y divide-line">
          {data.items.map((item) => (
            <NotificationEntry
              key={item.id}
              item={item}
              onOpen={(entry) => {
                setOpen(false);
                void openNotification(entry);
              }}
            />
          ))}
        </ul>
      ) : (
        <EmptyState
          icon="bell"
          title={t('shell.notifications.emptyTitle')}
          description={t('shell.notifications.emptyDescription')}
        />
      )}
    </ResponsivePopover>
  );
}
